import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPositions, exitPositions } from '../api';

const REFRESH_INTERVAL = 10000;

export default function Positions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [exiting, setExiting] = useState(false);
  const [filterAccount, setFilterAccount] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await fetchPositions();
      if (res.data?.status === 'ok') setData(res.data);
    } catch {}
    setLoading(false);
    setLastRefresh(new Date().toLocaleTimeString());
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  const toggleSelect = (posId) => {
    setSelected(prev => ({ ...prev, [posId]: !prev[posId] }));
  };

  const getFilteredPositions = () => {
    if (!data?.positions) return [];
    return data.positions.filter(p => {
      if (filterAccount && p.account_name !== filterAccount) return false;
      if (filterStatus === 'open' && p.net_qty === 0) return false;
      if (filterStatus === 'closed' && p.net_qty !== 0) return false;
      return true;
    });
  };

  const selectAll = () => {
    const filtered = getFilteredPositions().filter(p => p.net_qty !== 0);
    const allSelected = filtered.every(p => selected[p.id]);
    const next = { ...selected };
    filtered.forEach(p => { next[p.id] = !allSelected; });
    setSelected(next);
  };

  const handleExit = async () => {
    const toExit = getFilteredPositions().filter(p => selected[p.id] && p.net_qty !== 0);
    if (toExit.length === 0) return;
    const seen = new Set();
    const payload = [];
    for (const p of toExit) {
      const key = p.strategy_name;
      if (!seen.has(key)) {
        seen.add(key);
        payload.push({ strategy_name: p.strategy_name, position_index: -1 });
      }
    }
    if (!window.confirm(`Exit ${payload.length} position(s)?`)) return;
    setExiting(true);
    try {
      await exitPositions({ positions: payload });
      setSelected({});
      await loadData();
    } catch {}
    setExiting(false);
  };

  const positions = getFilteredPositions();
  const openPositions = positions.filter(p => p.net_qty !== 0);
  const selectedCount = openPositions.filter(p => selected[p.id]).length;
  const accounts = data?.accounts || [];
  const accountNames = [...new Set((data?.positions || []).map(p => p.account_name))];
  const brokerDayPnl = data?.broker_day_pnl || null;

  const fmt = (v) => {
    if (v == null || isNaN(v)) return '0.00';
    return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtPnl = (v) => {
    if (v == null || isNaN(v)) return '0.00';
    const n = Number(v);
    const sign = n >= 0 ? '+' : '';
    return sign + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtAmt = (v) => {
    const n = Number(v) || 0;
    const sign = n >= 0 ? '+' : '-';
    const abs = Math.abs(n);
    if (abs >= 100000) return sign + '\u20B9' + (abs / 100000).toFixed(2) + 'L';
    return sign + '\u20B9' + abs.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const pnlColor = (v) => (Number(v) || 0) >= 0 ? '#22c55e' : '#ef4444';
  const pnlClass = (v) => (Number(v) || 0) >= 0 ? 'positive' : 'negative';

  if (loading && !data) {
    return <div className="loading"><div className="spinner" /><p>Loading positions...</p></div>;
  }

  return (
    <div className="page-positions">
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Positions</h1>
          <p className="page-subtitle">Position book & premium tracking</p>
        </div>
        {lastRefresh && (
          <span className="live-indicator">
            <span className="live-pulse" /> Updated {lastRefresh}
          </span>
        )}
      </div>

      <div className="hub-tabs">
        <button className="hub-tab hub-tab-active">Position Book</button>
        <button className="hub-tab" onClick={() => navigate('/premium-chart')}>Premium Charts</button>
        <button className="hub-tab" onClick={() => navigate('/option-chain')}>Option Chain</button>
      </div>

      {/* Day P&L Summary */}
      <section className="sl-stats-bar">
        {brokerDayPnl ? (
          <>
            <div className="sl-stat-item">
              <span className="sl-stat-label">Day P&amp;L</span>
              <span className={`sl-stat-value ${pnlClass(brokerDayPnl.total)}`} style={{ fontSize: '22px' }}>
                {fmtAmt(brokerDayPnl.total)}
              </span>
            </div>
            <div className="sl-stat-item">
              <span className="sl-stat-label">Realised</span>
              <span className={`sl-stat-value ${pnlClass(brokerDayPnl.realised)}`}>
                {fmtAmt(brokerDayPnl.realised)}
              </span>
            </div>
            <div className="sl-stat-item">
              <span className="sl-stat-label">Unrealised</span>
              <span className={`sl-stat-value ${pnlClass(brokerDayPnl.unrealised)}`}>
                {fmtAmt(brokerDayPnl.unrealised)}
              </span>
            </div>
          </>
        ) : (
          <div className="sl-stat-item">
            <span className="sl-stat-label">Total P&amp;L</span>
            <span className={`sl-stat-value ${pnlClass(positions.reduce((s, p) => s + (p.pnl || 0), 0))}`}>
              {fmtAmt(positions.reduce((s, p) => s + (p.pnl || 0), 0))}
            </span>
          </div>
        )}
        <div className="sl-stat-item">
          <span className="sl-stat-label">Open</span>
          <span className="sl-stat-value sl-active-val">{openPositions.length}</span>
        </div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Closed</span>
          <span className="sl-stat-value">{positions.length - openPositions.length}</span>
        </div>
        {selectedCount > 0 && (
          <div className="sl-stat-item">
            <button
              className="btn btn-danger"
              onClick={handleExit}
              disabled={exiting || selectedCount === 0}
              style={{ padding: '8px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer', background: '#ef4444', color: '#fff', border: 'none' }}
            >
              {exiting ? 'Exiting...' : `Exit Selected (${selectedCount})`}
            </button>
          </div>
        )}
      </section>

      {/* Filters */}
      <div className="inst-filters">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Positions</option>
          <option value="open">Open Only</option>
          <option value="closed">Closed Only</option>
        </select>
        <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
          <option value="">All Accounts</option>
          {accountNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button className="dash-view-all" onClick={selectAll} style={{ marginLeft: 'auto' }}>
          {openPositions.every(p => selected[p.id]) && openPositions.length > 0 ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      {/* Account Groups */}
      {accounts.map(acct => {
        const acctPositions = positions.filter(p => p.account_name === acct.account_name);
        if (acctPositions.length === 0) return null;
        const acctOpen = acctPositions.filter(p => p.net_qty !== 0);
        const acctClosed = acctPositions.filter(p => p.net_qty === 0);
        return (
          <section key={acct.account_name} className="sl-group">
            <h2 className="sl-group-title">
              <span className="sl-group-icon">👤</span> {acct.account_name}
              {acct.broker_day_pnl && (
                <span style={{ fontSize: '14px', marginLeft: '12px' }}>
                  <span style={{ color: pnlColor(acct.broker_day_pnl.total), fontWeight: 700 }}>
                    {fmtAmt(acct.broker_day_pnl.total)}
                  </span>
                  <span style={{ color: '#888', marginLeft: '8px', fontSize: '12px' }}>
                    (R: {fmtAmt(acct.broker_day_pnl.realised)} | U: {fmtAmt(acct.broker_day_pnl.unrealised)})
                  </span>
                </span>
              )}
              <span style={{ fontSize: '12px', color: '#888', marginLeft: '12px' }}>
                {acctOpen.length} open · {acctClosed.length} closed
              </span>
            </h2>
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="trade-table-v2" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th style={{ width: '32px' }}>
                      <input
                        type="checkbox"
                        checked={acctOpen.length > 0 && acctOpen.every(p => selected[p.id])}
                        onChange={() => {
                          const allSel = acctOpen.every(p => selected[p.id]);
                          const next = { ...selected };
                          acctOpen.forEach(p => { next[p.id] = !allSel; });
                          setSelected(next);
                        }}
                      />
                    </th>
                    <th>Symbol</th>
                    <th>Product</th>
                    <th style={{ textAlign: 'right' }}>Buy Qty</th>
                    <th style={{ textAlign: 'right' }}>Buy Avg</th>
                    <th style={{ textAlign: 'right' }}>Sell Qty</th>
                    <th style={{ textAlign: 'right' }}>Sell Avg</th>
                    <th style={{ textAlign: 'right' }}>Net Qty</th>
                    <th style={{ textAlign: 'right' }}>LTP</th>
                    <th style={{ textAlign: 'right' }}>P&amp;L</th>
                    <th style={{ textAlign: 'right' }}>Realised</th>
                    <th style={{ textAlign: 'right' }}>Unrealised</th>
                    <th>Source</th>
                    <th style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {acctPositions.map(pos => {
                    const isOpen = pos.net_qty !== 0;
                    const rowStyle = !isOpen ? { opacity: 0.5 } : {};
                    return (
                      <tr key={pos.id} className={selected[pos.id] ? 'selected-row' : ''} style={rowStyle}>
                        <td>
                          {isOpen && (
                            <input
                              type="checkbox"
                              checked={!!selected[pos.id]}
                              onChange={() => toggleSelect(pos.id)}
                            />
                          )}
                        </td>
                        <td style={{ fontWeight: 600 }}>
                          {pos.tradingsymbol}
                          <span style={{ color: '#888', fontSize: '10px', marginLeft: '4px' }}>{pos.exchange}</span>
                        </td>
                        <td>
                          <span style={{
                            background: pos.product === 'NRML' || pos.product === 'nrml' ? '#1e40af22' : '#7c3aed22',
                            color: pos.product === 'NRML' || pos.product === 'nrml' ? '#60a5fa' : '#a78bfa',
                            padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600
                          }}>
                            {(pos.product || 'NRML').toUpperCase()}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', color: pos.buy_qty > 0 ? '#56d364' : '#666' }}>
                          {pos.buy_qty || '-'}
                        </td>
                        <td style={{ textAlign: 'right' }}>{pos.buy_avg > 0 ? fmt(pos.buy_avg) : '-'}</td>
                        <td style={{ textAlign: 'right', color: pos.sell_qty > 0 ? '#f85149' : '#666' }}>
                          {pos.sell_qty || '-'}
                        </td>
                        <td style={{ textAlign: 'right' }}>{pos.sell_avg > 0 ? fmt(pos.sell_avg) : '-'}</td>
                        <td style={{
                          textAlign: 'right',
                          fontWeight: 700,
                          color: pos.net_qty === 0 ? '#666' : pos.net_qty < 0 ? '#f85149' : '#56d364',
                        }}>
                          {pos.net_qty}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(pos.ltp)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: pnlColor(pos.pnl) }}>
                          {fmtPnl(pos.pnl)}
                        </td>
                        <td style={{ textAlign: 'right', color: pnlColor(pos.realised) }}>
                          {fmtPnl(pos.realised)}
                        </td>
                        <td style={{ textAlign: 'right', color: pnlColor(pos.unrealised) }}>
                          {fmtPnl(pos.unrealised)}
                        </td>
                        <td>
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: pos.source === 'system' ? '#16a34a22' : '#ca8a0422',
                            color: pos.source === 'system' ? '#4ade80' : '#fbbf24',
                          }}>
                            {pos.source === 'system' ? 'System' : 'User'}
                          </span>
                        </td>
                        <td>
                          {isOpen && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Exit position ${pos.tradingsymbol}?`)) return;
                                setExiting(true);
                                try {
                                  await exitPositions({
                                    positions: [{ strategy_name: pos.strategy_name, position_index: -1 }],
                                  });
                                  await loadData();
                                } catch {}
                                setExiting(false);
                              }}
                              disabled={exiting}
                              style={{
                                padding: '3px 10px', fontSize: '11px', background: '#ef4444',
                                color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer',
                              }}
                            >
                              Exit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {positions.length === 0 && (
        <div className="dash-empty">No positions</div>
      )}
    </div>
  );
}
